import { appColors } from "@/constants/color";
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import "./fileUploader.css";
const defaultImage = "/icons/defaultImg.png";
import { FileTypeEnum } from "@/enum";
import { useUploadFileMutation } from "@/features/file";

interface FileUploaderProps {
  images?: string[];
  multiple?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  allowedTypes?: string[];
  onFilesSelected?: (files: string[]) => void;
  onError?: (error: string) => void;
  type: FileTypeEnum;
  uploadHeaders?: Record<string, string>;
}

export interface FileUploaderRef {
  openPicker: () => void;
  clearFiles: () => void;
  getFiles: () => string[];
  removeFile: (url: string) => void;
}

export const FileUploader = forwardRef<FileUploaderRef, FileUploaderProps>(
  (
    {
      images = [],
      multiple = false,
      maxFiles = 10,
      maxFileSize = 10, // default → 10MB
      allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
      onFilesSelected,
      onError,
      uploadHeaders,
      type
    },
    ref
  ) => {
    const [files, setFiles] = useState<string[]>(images);
    const [currentImage, setCurrentImage] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{
      [key: string]: number;
    }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showEditMenu, setShowEditMenu] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      openPicker: () => fileInputRef.current?.click(),
      clearFiles: () => {
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setErrorMessage(null);
      },
      getFiles: () => files,
      removeFile,
    }));

    const validateFile = (file: File): boolean => {
      // Clear previous errors
      setErrorMessage(null);

      // Validate file type
      const validTypes = allowedTypes || ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      const isValidType = validTypes.some(type => {
        if (type.endsWith("/*")) {
          const category = type.split("/")[0];
          return file.type.startsWith(category);
        }
        return file.type === type;
      });

      if (!isValidType) {
        const msg = `Invalid file type: ${file.type}. Allowed types: ${validTypes.join(", ")}`;
        setErrorMessage(msg);
        onError?.(msg);
        return false;
      }

      // Validate file size (10MB max)
      const maxSizeBytes = (maxFileSize || 10) * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        const msg = `File too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Max size: ${maxFileSize}MB`;
        setErrorMessage(msg);
        onError?.(msg);
        return false;
      }

      return true;
    };
    const [fileUpload] = useUploadFileMutation()
    const uploadFileToServer = async (file: File): Promise<string | null> => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      try {
        const response = await fileUpload({ type, formData }).unwrap();
        return response.data?.url as string;
      } catch (error) {
        console.error("Upload failed:", error);
        const msg = `Failed to upload ${file.name}: ${error instanceof Error ? error.message : "Unknown error"}`;
        setErrorMessage(msg);
        onError?.(msg);
        return null;
      }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (!selectedFiles || selectedFiles.length === 0) return;

      // Clear previous errors
      setErrorMessage(null);

      // Validate selection count
      if (!multiple && selectedFiles.length > 1) {
        const msg = "Only single file allowed";
        setErrorMessage(msg);
        onError?.(msg);
        return;
      }

      if (maxFiles && files.length + selectedFiles.length > maxFiles) {
        const msg = `Maximum ${maxFiles} files allowed`;
        setErrorMessage(msg);
        onError?.(msg);
        return;
      }

      // Validate each file
      const validFiles: File[] = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        if (validateFile(selectedFiles[i])) {
          validFiles.push(selectedFiles[i]);
        } else {
          // Stop processing if any file is invalid
          break;
        }
      }

      if (validFiles.length === 0) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setUploading(true);
      try {
        const uploadResults = await Promise.all(
          validFiles.map(async (file) => await uploadFileToServer(file))
        );

        const successfulUploads = uploadResults.filter(
          (result) => result !== null
        ) as string[];
        const updatedFiles = multiple
          ? [...files, ...successfulUploads]
          : successfulUploads.length > 0
            ? [successfulUploads[0]]
            : [];

        setFiles(updatedFiles);
        onFilesSelected?.(updatedFiles);
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Upload failed";
        setErrorMessage(msg);
        onError?.(msg);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    const removeFile = (url: string) => {
      const newFiles = files.filter((file) => file !== url);
      setFiles(newFiles);
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[url];
        return newProgress;
      });
      onFilesSelected?.(newFiles);
      setErrorMessage(null);
    };

    const openImagePreview = (url: string) => {
      setCurrentImage(url);
    };

    const closeImagePreview = () => {
      setCurrentImage(null);
    };

    const handleChooseFromGallery = () => {
      setShowEditMenu(false);
      fileInputRef.current?.click();
    };

    const handleTakePhoto = () => {
      setShowEditMenu(false);
      cameraInputRef.current?.click();
    };

    const handleRemove = () => {
      setShowEditMenu(false);
      if (files.length > 0) {
        removeFile(files[0]);
      }
    };

    const handleCancel = () => {
      setShowEditMenu(false);
    };

    useEffect(() => {
      if (JSON.stringify(files) !== JSON.stringify(images)) {
        setFiles(images);
      }
    }, [images]);
    return (
      <div className="fileUploaderContainer">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={allowedTypes.join(",")}
          multiple={multiple}
          style={{ display: "none" }}
        />
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
        />

        {/* Error message positioned below the main preview */}
        {errorMessage && (
          <div className="errorText" style={{
            marginTop: "10px",
            color: appColors.fileuploaderText,
            textAlign: "center"
          }}>
            {errorMessage}
          </div>
        )}

        <div className="mainAddBoxWrapper">
          <div
            className="mainAddBox"
            onClick={() =>
              !uploading && files.length === 0 && fileInputRef.current?.click()
            }
          >
            {uploading ? (
              <div className="uploadingIndicator">Uploading...</div>
            ) : files.length > 0 ? (
              <img
                src={files[0]}
                alt="Main preview"
                className="mainPreviewImage"
                onClick={() => openImagePreview(files[0])}
              />
            ) : (
              <>
                <img src={defaultImage} className="defaultImage" />
                <div className="mainAddText">Add Image</div>
              </>
            )}
          </div>
          
          {/* Edit icon at right corner of image when logo exists */}
          {files.length > 0 && !uploading && (
            <div className="editIconWrapper">
              <button
                className="editIconButton"
                onClick={() => setShowEditMenu(!showEditMenu)}
                title="Edit logo"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              
              {/* Edit menu options */}
              {showEditMenu && (
                <>
                  <div className="editMenuBackdrop" onClick={handleCancel} />
                  <div className="editMenu">
                    <button className="editMenuItem" onClick={handleChooseFromGallery}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
                      </svg>
                      Choose from gallery
                    </button>
                    <button className="editMenuItem" onClick={handleTakePhoto}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 17.5c2.33 0 4.25-1.92 4.25-4.25S14.33 9 12 9s-4.25 1.92-4.25 4.25S9.67 17.5 12 17.5zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z" fill="currentColor"/>
                      </svg>
                      Take a photo
                    </button>
                    <button className="editMenuItem editMenuItemDanger" onClick={handleRemove}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                      </svg>
                      Remove
                    </button>
                    <button className="editMenuItem" onClick={handleCancel}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                      </svg>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="thumbnailContainer">
          {files.map((file, index) => (
            <div key={`${file}-${index}`} className="filePreview">
              <img
                src={file}
                alt={`Preview ${index}`}
                className="thumbnailImage"
                onClick={() => openImagePreview(file)}
              />
              <button
                className="removeButton"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file);
                }}
              >
                ×
              </button>
              {uploadProgress[file] && uploadProgress[file] < 100 && (
                <div className="progressContainer">
                  <div
                    className="progressBar"
                    style={{ width: `${uploadProgress[file]}%` }}
                  />
                  <div className="progressText">{uploadProgress[file]}%</div>
                </div>
              )}
            </div>
          ))}

          {(multiple || files.length === 0) &&
            files.length < maxFiles &&
            !uploading && (
              <div
                className="addMoreBox"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="addIcon">+</div>
              </div>
            )}
        </div>

        {currentImage && (
          <div className="imagePreviewModal" onClick={closeImagePreview}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <button className="closeButton" onClick={closeImagePreview}>
                ×
              </button>
              <img
                src={currentImage}
                alt="Full size preview"
                className="fullSizeImage"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);